import type { MerchantLocation } from '../compliance/location.js';

/**
 * Who a campaign skips, and why (M9, owner decisions 2026-10-14 #5–#8, #6 changed by the owner). Pure:
 * the launch snapshot and the fan-out both decide with it, so the review screen's counts are the rules
 * the texts are queued by. Every skipped merchant gets one reason, the first that applies in this order.
 * The pacer still checks everything again at the final gate; this only keeps texts from being queued that
 * could never (or should never) go out.
 */

export const SKIP_REASONS = [
  'opted_out',
  'do_not_contact',
  'suppressed',
  'archived',
  'landline',
  'outside_us',
  'needs_state',
  'no_consent',
  'consent_not_campaign_eligible',
  'in_other_campaign',
  'recently_texted',
  'too_long',
  'campaign_cancelled',
  'expired',
] as const;
export type SkipReason = (typeof SKIP_REASONS)[number];

export const SKIP_REASON_LABELS: Record<SkipReason, string> = {
  opted_out: 'Opted out',
  do_not_contact: 'Do not contact',
  suppressed: 'On the suppression list',
  archived: 'Archived',
  landline: 'Landline',
  outside_us: 'Outside the US',
  needs_state: 'Needs a state',
  no_consent: 'No consent on file',
  consent_not_campaign_eligible: "Consent doesn't cover campaigns",
  in_other_campaign: 'Waiting on another campaign',
  recently_texted: 'Got a campaign text recently',
  too_long: 'Text too long',
  campaign_cancelled: 'Campaign cancelled',
  expired: 'Not sent in time',
};

/**
 * Consent sources that count for campaigns by default (owner decision 2026-10-14, change to #6): web
 * form, written, vendor, import and api. Verbal and "other" do not, by default; `inbound_text` (replies
 * only) and `sms_start` (lifts an opt-out) never do. The workspace's list is data
 * (`workspaces.campaign_consent_sources`), changed only by the database owner after counsel's review.
 */
export const DEFAULT_CAMPAIGN_CONSENT_SOURCES = [
  'web_form',
  'written',
  'vendor',
  'import',
  'api',
] as const;

/** Every source the workspace list may contain. inbound_text and sms_start can never count. */
export const CAMPAIGN_CONSENT_SOURCE_OPTIONS = [
  'web_form',
  'written',
  'vendor',
  'verbal',
  'other',
  'import',
  'api',
] as const;

export const CONSENT_SOURCE_LABELS: Record<string, string> = {
  web_form: 'Web form',
  written: 'Written',
  vendor: 'Lead vendor',
  verbal: 'Verbal',
  other: 'Other',
  import: 'Import (with attestation)',
  api: 'Webhook lead',
  inbound_text: 'Merchant texted first',
  sms_start: 'Texted START',
};

/** Reasons that mean the number must never be texted. */
const DNC_REASONS = new Set(['do_not_contact', 'manual', 'vendor_dnc']);

/** What the database says about one merchant in the audience. */
export interface AudienceCandidate {
  archived: boolean;
  optedOut: boolean;
  doNotContact: boolean;
  lineType: string;
  /** Active suppression reasons for the merchant's phone: ANY reason blocks, known or not. */
  suppression: readonly string[];
  /** Sources of the merchant's consent records. */
  consentSources: readonly string[];
  /** A text from another running or paused campaign that has not gone out or been dropped yet. */
  openInOtherCampaign: boolean;
  /** A campaign text handed to the provider within the campaign's frequency window. */
  recentlyTexted: boolean;
}

export interface EligibilityContext {
  /** The workspace's campaign-eligible consent sources. */
  consentSources: ReadonlySet<string>;
  location: MerchantLocation;
}

/** The first reason this merchant is skipped, or null when the campaign may queue a text for them. */
export function skipReason(c: AudienceCandidate, ctx: EligibilityContext): SkipReason | null {
  if (c.suppression.length > 0) {
    if (c.suppression.includes('opt_out')) return 'opted_out';
    if (c.suppression.some((r) => DNC_REASONS.has(r))) return 'do_not_contact';
    return 'suppressed';
  }
  if (c.optedOut) return 'opted_out';
  if (c.doNotContact) return 'do_not_contact';
  if (c.archived) return 'archived';
  if (c.lineType === 'landline') return 'landline';
  if (!ctx.location.ok) return ctx.location.action === 'block' ? 'outside_us' : 'needs_state';
  if (c.consentSources.length === 0) return 'no_consent';
  if (!c.consentSources.some((s) => ctx.consentSources.has(s))) {
    return 'consent_not_campaign_eligible';
  }
  if (c.openInOtherCampaign) return 'in_other_campaign';
  if (c.recentlyTexted) return 'recently_texted';
  return null;
}

export type SkipCounts = Record<SkipReason, number>;

export function emptySkipCounts(): SkipCounts {
  return Object.fromEntries(SKIP_REASONS.map((r) => [r, 0])) as SkipCounts;
}
